import * as THREE from 'three/webgpu';
import {SketchRelationContainer} from '../solver/constraint/SketchRelationContainer';
import {detachConstraintFromAllTargets} from '../solver/constraint/utils/detachConstraintFromAllTargets';
import {SketchEntityKind} from '../solver/constraint/relation-compatibility/SketchEntityKinds';
import type {ISketchObject} from '../../mesh/sketch/ISketchObject';
import type {SketchCloneContext} from '../../mesh/sketch/SketchCloneContext';
import type {SketchGroup} from '../../mesh/sketch/SketchGroup';
import type {ReferencePlane} from '../../reference/ReferencePlane';
import {createSketchEntityReferenceId} from '../../reference/sketch-entity-reference/model/SketchEntityReferenceIds';
import {createSketchEntityPersistentId} from '../identity/SketchPersistentIdFactory';
import type {
    MirrorAxisGeometryProvider,
    SketchMirrorAxisReference,
    SketchMirrorAxisReferenceProviderOptions,
    SketchMirrorAxisReferenceResolverPort,
    SketchMirrorAxisResolvedGeometry,
    SketchPersistentMirrorAxisReferenceRecord,
} from '../../../types/sketch/mirror/SketchMirrorAxisReferenceTypes';
import {SKETCH_GEOMETRY_TOLERANCE_CONFIG} from '../config/SketchGeometryToleranceConfig';

/**
 * 外部镜像轴稳定引用对象。
 *
 * 职责与生命周期：
 * 1. 作为实体级对称关系的正式组内目标，承载独立 persistentId 和约束容器；
 * 2. 只保存可重解析的来源身份，不缓存一次性世界坐标轴；
 * 3. resolver 随 Viewer 运行时绑定，文档恢复时允许暂时悬空，来源恢复后可重新绑定；
 * 4. 本对象不创建渲染几何或材质，dispose 仅解除运行时解析器和关系引用。
 */
export class SketchMirrorAxisReferenceProvider extends THREE.Object3D implements MirrorAxisGeometryProvider {
    /** 外部镜像轴对象领域标记。 */
    public readonly isSketchMirrorAxisReferenceProvider = true;
    /** 草图对象统一标记。 */
    public readonly isSketchObject = true;
    /** 外部引用不是封闭草图几何。 */
    public readonly isClosedGeometry = false;
    /** 关系兼容层中的对象种类。 */
    public readonly objectType = SketchEntityKind.ReferenceAxis;
    /** 关系 targetIds 使用的跨会话稳定身份。 */
    public persistentId = createSketchEntityPersistentId();
    /** 关系容器随 provider 生命周期存在。 */
    public readonly constraints: SketchRelationContainer<this> = new SketchRelationContainer(this);
    /** 关系所属草图参考平面。 */
    public readonly referencePlane: ReferencePlane;
    /** 不含运行时世界轴的稳定来源。 */
    private referenceInternal: SketchMirrorAxisReference;
    /** 可替换的 Viewer 运行时解析端口。 */
    private resolver: SketchMirrorAxisReferenceResolverPort | null;
    /** 正式拥有本引用目标的草图；引用目标不进入 Object3D 场景树。 */
    private ownerSketchGroup: SketchGroup | null = null;
    /** 同一同步求解事务的解析作用域深度。 */
    private resolutionScopeDepth = 0;
    /** 当前作用域是否已经解析过来源，null 也属于有效缓存结果。 */
    private hasScopedResolution = false;
    /** 当前同步作用域内的轴几何；作用域结束后立即清空，禁止跨模型 revision 缓存。 */
    private scopedResolution: SketchMirrorAxisResolvedGeometry | null = null;
    /** provider 独占的世界轴输出，避免每次关系求解创建两支 Vector3。 */
    private readonly resolvedGeometryScratch: SketchMirrorAxisResolvedGeometry = {
        startWorld: new THREE.Vector3(),
        endWorld: new THREE.Vector3(),
    };
    /** 防止三端解绑期间生命周期钩子重入重复释放同一 provider。 */
    private isDisposed = false;

    /**
     * @param options 稳定引用、目标草图平面与可选运行时解析器。
     */
    public constructor(options: SketchMirrorAxisReferenceProviderOptions) {
        super();
        this.referencePlane = options.referencePlane;
        this.referenceInternal = structuredClone(options.reference);
        if (this.referenceInternal.source.kind === 'topology-edge') {
            // SDK 调用方也只能提交稳定拓扑身份；世界端点快照不得穿过 provider 进入历史或持久化。
            delete this.referenceInternal.source.descriptor.locator.geomFingerprint;
        }
        this.resolver = options.resolver ?? null;
        this.name = this.resolveDisplayName();
        // provider 只是关系目标，不创建不可见线或其它渲染替身。
        this.visible = false;
        this.frustumCulled = false;
    }

    /** @returns 与运行时可变状态隔离的稳定引用副本。 */
    public getReference(): SketchMirrorAxisReference {
        return structuredClone(this.referenceInternal);
    }

    /**
     * 从稳定引用确定可跨重载复现的选择显示名。
     * @returns 可区分基准面、模型边和模型面的中文名称。
     */
    private resolveDisplayName(): string {
        const source = this.referenceInternal.source;
        if (source.kind === 'datum-plane') {
            return `基准面 ${source.datumPlaneId}`;
        }
        if (source.kind === 'topology-edge') {
            const stableIdentity = source.descriptor.locator.persistentRef
                ?? source.descriptor.locator.subEntityId
                ?? source.descriptor.owner.ownerId;
            return `模型边 ${source.descriptor.locator.edgeIndex + 1} · ${this.resolveStableIdentitySuffix(stableIdentity)}`;
        }
        const stableIdentity = source.face.locator.semanticName
            ?? source.face.locator.persistentRef
            ?? source.face.locator.subEntityId
            ?? source.face.owner.ownerId;
        return `平面面 ${source.face.locator.faceIndex + 1} · ${this.resolveStableIdentitySuffix(stableIdentity)}`;
    }

    /**
     * 截取稳定身份末段，避免属性栏展示过长引用，同时不依赖运行时 uuid。
     * @param stableIdentity 持久拓扑或 owner 身份。
     * @returns 最多八个字符的稳定后缀。
     */
    private resolveStableIdentitySuffix(stableIdentity: string): string {
        return stableIdentity.slice(-8);
    }

    /**
     * 绑定当前 Viewer 的解析器。
     * @param resolver 可重解析模型或基准来源的运行时端口。
     */
    public bindMirrorAxisReferenceResolver(resolver: SketchMirrorAxisReferenceResolverPort): void {
        this.resolver = resolver;
    }

    /** 解除运行时解析器，稳定引用仍保留为可恢复悬空目标。 */
    public unbindMirrorAxisReferenceResolver(): void {
        this.resolver = null;
    }

    /**
     * 由 SketchGroup 独立引用目标注册表建立唯一所有权。
     *
     * @param sketchGroup 正式拥有当前 provider 的草图。
     * @throws provider 已释放或已属于其它草图时抛出错误。
     */
    public bindSketchReferenceTargetOwner(sketchGroup: SketchGroup): void {
        if (this.isDisposed) {
            throw new Error(`镜像轴外部引用已释放，不能重新挂入草图：providerId=${this.persistentId}`);
        }
        if (this.ownerSketchGroup && this.ownerSketchGroup !== sketchGroup) {
            throw new Error(`镜像轴外部引用已属于其它草图：providerId=${this.persistentId}`);
        }
        this.ownerSketchGroup = sketchGroup;
    }

    /**
     * 由当前所有者解除独立引用目标注册。
     *
     * @param sketchGroup 发起解除的草图。
     * @throws 发起解除的草图与当前所有者不一致时抛出错误。
     */
    public unbindSketchReferenceTargetOwner(sketchGroup: SketchGroup): void {
        if (this.ownerSketchGroup && this.ownerSketchGroup !== sketchGroup) {
            throw new Error(`镜像轴外部引用所有者不匹配：providerId=${this.persistentId}`);
        }
        this.ownerSketchGroup = null;
    }

    /** @returns 当前合法世界轴；来源悬空或二维规则不成立时返回 null。 */
    public resolveMirrorAxisGeometry(): SketchMirrorAxisResolvedGeometry | null {
        if (this.resolutionScopeDepth > 0 && this.hasScopedResolution) {
            return this.scopedResolution;
        }
        const geometry = this.resolver?.resolveMirrorAxisReference(
            this.referenceInternal,
            this.referencePlane,
            this.resolvedGeometryScratch,
        ) ?? null;
        const resolvedGeometry = geometry
            && geometry.startWorld.distanceToSquared(geometry.endWorld)
                > SKETCH_GEOMETRY_TOLERANCE_CONFIG.lineDirectionLengthSquared
            ? geometry
            : null;
        if (this.resolutionScopeDepth > 0) {
            this.scopedResolution = resolvedGeometry;
            this.hasScopedResolution = true;
        }
        // resolver 每次返回当前来源的求解结果；关系与快照只读消费，避免热路径再复制两支 Vector3。
        return resolvedGeometry;
    }

    /**
     * 在一次同步求解事务中复用同一个来源解析结果。
     *
     * 缓存只存在于回调栈内，finally 会无条件清空，因此模型重建或下一次拖拽仍会读取当前 owner。
     * @param work 需要共享本轮轴几何的同步工作。
     * @returns 回调结果。
     */
    public runWithMirrorAxisResolutionScope<TResult>(work: () => TResult): TResult {
        this.resolutionScopeDepth += 1;
        try {
            return work();
        } finally {
            this.resolutionScopeDepth -= 1;
            if (this.resolutionScopeDepth === 0) {
                this.hasScopedResolution = false;
                this.scopedResolution = null;
            }
        }
    }

    /** @returns 草图文档可直接持久化的目标记录。 */
    public toPersistentRecord(): SketchPersistentMirrorAxisReferenceRecord {
        return {
            targetId: this.persistentId,
            reference: this.getReference(),
        };
    }

    /**
     * 为复制出的新逻辑草图生成独立引用生命周期 ID。
     * provider 的 target persistentId 由 SketchGroup 统一换新，本方法只换引用记录自身 ID。
     */
    public regenerateReferenceIdentity(): void {
        this.referenceInternal = {
            ...this.referenceInternal,
            referenceId: createSketchEntityReferenceId(),
        };
    }

    /**
     * 克隆运行时对象；历史快照继承稳定身份，真正复制草图时由 SketchGroup 统一换新。
     * @param context 草图克隆上下文。
     * @returns 完全独立但指向同一外部来源的 provider。
     */
    public cloneSelf(context?: SketchCloneContext): SketchMirrorAxisReferenceProvider {
        const clone = new SketchMirrorAxisReferenceProvider({
            reference: this.referenceInternal,
            referencePlane: this.referencePlane,
            resolver: this.resolver,
        });
        clone.persistentId = this.persistentId;
        context?.objectMap.set(this.uuid, clone);
        return clone;
    }

    /** 外部引用目标不可拖动。 */
    public canDrag(): boolean {
        return false;
    }

    /**
     * 外部引用不是可拖动实体，错误调用时显式失败，避免交互层误把它当作隐藏草图线。
     * @throws 始终抛出不可拖动错误。
     */
    public beginDragPreview(): void {
        throw new Error('镜像轴外部引用不可进入拖拽预览。');
    }

    /**
     * 外部引用从不进入拖拽预览，错误结束调用同样显式失败。
     * @throws 始终抛出未进入拖拽预览错误。
     */
    public endDragPreview(): void {
        throw new Error('镜像轴外部引用未进入拖拽预览。');
    }

    /**
     * 释放 provider 持有的运行时与关系引用。
     * 调用边界为草图被真正销毁；Undo/Redo 的临时离场不得调用。
     */
    public dispose(): void {
        if (this.isDisposed) {
            return;
        }
        // 即使调用方直接释放 provider，也必须先退出正式引用目标域，避免留下悬空身份索引。
        this.ownerSketchGroup?.removeSketchReferenceTarget(this);
        this.isDisposed = true;
        const constraints = this.constraints.getRelations();
        // 同一多目标关系同时登记在轴、源实体和镜像实体上；释放必须从全部目标解除。
        constraints.forEach((constraint) => {
            detachConstraintFromAllTargets(constraint);
        });
        this.resolver = null;
    }
}
